import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Chip,
  Box,
} from "@mui/material";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiMessageSquare,
  FiExternalLink,
  FiPlus,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { showError, showSuccess } from "../utils/toast";
import { Toaster } from "react-hot-toast";
import { colors } from "../theme/modernTheme";
import { useDebounce } from "../hooks/useDebounce";
import { whatsappTemplatesAPI } from "../services/api";
import { truncateText } from "../utils/truncate";
import TableLoader from "../components/TableLoader";
import { getWhatsAppTemplateTypeIcon } from "../components/WhatsAppTemplateTypeIcon";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import WhatsAppTemplateDeleteContent from "../components/WhatsAppTemplateDeleteContent";
import { useAuth } from "../context/AuthContext";
import { MODULE_PERMISSIONS } from "../utils/modulePermissions";
import { formatDateForDisplay } from "../utils/dateUtils";

export default function WhatsAppTemplates() {
  const navigate = useNavigate();
  const { hasWritePermission } = useAuth();
  
  // RBAC: Check if user has write permission for WhatsApp templates
  const canWrite = hasWritePermission(MODULE_PERMISSIONS.WHATSAPP_TEMPLATE);
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businessId, setBusinessId] = useState("");
  const [wabaId, setWabaId] = useState("");

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [languageInput, setLanguageInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("");

  // Applied filter states (used for API calls)
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Cursor-based pagination states
  const [pagination, setPagination] = useState({
    limit: 10,
    nextCursor: null,
    previousCursor: null,
    hasNext: false,
    hasPrevious: false,
  });
  const [currentCursor, setCurrentCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const loadTemplates = useCallback(
    async (cursor = null, direction = null) => {
      setLoading(true);
      try {
        const params = {
          limit: pagination.limit,
        };

        // Add cursor parameters based on direction
        if (cursor && direction === "next") {
          params.after = cursor;
        } else if (cursor && direction === "previous") {
          params.before = cursor;
        }

        if (debouncedSearchTerm.trim()) {
          params.search = debouncedSearchTerm.trim();
        }
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (languageFilter) {
          params.language = languageFilter;
        }
        if (categoryFilter) {
          params.category = categoryFilter;
        }

        const response = await whatsappTemplatesAPI.getAll(params);
        const {
          templates: fetchedTemplates = [],
          paging = {},
          business_id = "",
          waba_id = "",
        } = response?.data || {};

        setTemplates(fetchedTemplates);
        setBusinessId(business_id);
        setWabaId(waba_id);
        setCurrentCursor(cursor);

        // If templates count is less than limit, we know there's no next page
        const hasNextPage =
          fetchedTemplates.length >= pagination.limit && !!paging.next;

        setPagination((prev) => ({
          ...prev,
          nextCursor: paging.next || null,
          previousCursor: paging.previous || null,
          hasNext: hasNextPage,
          hasPrevious: !!paging.previous,
        }));
      } catch (error) {
        setTemplates([]);
        const errorMessage =
          error.error?.message || error.message || "Failed to load templates";
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      debouncedSearchTerm,
      statusFilter,
      languageFilter,
      categoryFilter,
      pagination.limit,
    ]
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleRowClick = (template) => {
    navigate(`/whatsapp-templates/${template.id}`);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setLoading(true);
    try {
      await whatsappTemplatesAPI.delete(templateToDelete.id, templateToDelete.name);
      showSuccess("Template deleted successfully");
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      // Reload templates
      loadTemplates();
    } catch (error) {
      showError(error.message || "Failed to delete template");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setSearchTerm(searchInput);
    setStatusFilter(statusInput);
    setLanguageFilter(languageInput);
    setCategoryFilter(categoryInput);
    setCurrentCursor(null);
    setCursorHistory([]);
    setPagination((prev) => ({
      ...prev,
      nextCursor: null,
      previousCursor: null,
      hasNext: false,
      hasPrevious: false,
    }));
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setStatusInput("");
    setLanguageInput("");
    setCategoryInput("");
    setSearchTerm("");
    setStatusFilter("");
    setLanguageFilter("");
    setCategoryFilter("");
    setCurrentCursor(null);
    setCursorHistory([]);
    setPagination((prev) => ({
      ...prev,
      nextCursor: null,
      previousCursor: null,
      hasNext: false,
      hasPrevious: false,
    }));
  };

  const handleNextPage = () => {
    if (pagination.hasNext && pagination.nextCursor) {
      // Store current cursor in history before moving forward
      setCursorHistory((prev) => [...prev, currentCursor]);
      loadTemplates(pagination.nextCursor, "next");
    }
  };

  const handlePreviousPage = () => {
    if (cursorHistory.length > 0) {
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory((prev) => prev.slice(0, -1));
      loadTemplates(previousCursor, "previous");
    } else {
      // If no history, load first page
      loadTemplates(null, null);
    }
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    searchInput || statusInput || languageInput || categoryInput;

  // Check if filters have changed from applied state
  const hasFilterChanges =
    searchInput !== searchTerm ||
    statusInput !== statusFilter ||
    languageInput !== languageFilter ||
    categoryInput !== categoryFilter;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return { bg: "#10B98115", color: "#059669" };
      case "pending":
        return { bg: "#F59E0B15", color: "#D97706" };
      case "rejected":
        return { bg: "#EF444415", color: "#DC2626" };
      default:
        return { bg: colors.action.hover, color: colors.text.secondary };
    }
  };

  return (
    <Box>
      <Toaster />

      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          WhatsApp Templates
        </Typography>
        <Box display="flex" gap={2}>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<FiPlus />}
              onClick={() => navigate("/whatsapp-templates/create")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                backgroundColor: colors.primary.main,
                "&:hover": {
                  backgroundColor: colors.primary.dark,
                },
              }}
            >
              Create Template
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FiExternalLink />}
            disabled={!businessId || !wabaId}
            onClick={() =>
              window.open(
                `https://business.facebook.com/latest/whatsapp_manager/message_templates?business_id=${businessId}&asset_id=${wabaId}`,
                "_blank"
              )
            }
            sx={{
              fontWeight: 600,
              borderRadius: "4px",
              borderColor: colors.primary.main,
              color: colors.primary.main,
              "&:hover": {
                borderColor: colors.primary.dark,
                backgroundColor: `${colors.primary.main}10`,
              },
            }}
          >
            Manage Templates
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "4px",
          boxShadow: "none",
        }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search templates..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput("");
                      setSearchTerm("");
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    sx={{ padding: "4px" }}>
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch
                    style={{ marginRight: 8, color: colors.text.secondary }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.background.paper,
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    "& .MuiMenu-list": {
                      padding: "0px",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}>
              <MenuItem
                value=""
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                All
              </MenuItem>
              <MenuItem
                value="approved"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Approved
              </MenuItem>
              <MenuItem
                value="pending"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Pending
              </MenuItem>
              <MenuItem
                value="rejected"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Rejected
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Language"
              value={languageInput}
              onChange={(e) => setLanguageInput(e.target.value)}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    "& .MuiMenu-list": {
                      padding: "0px",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}>
              <MenuItem
                value=""
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                All
              </MenuItem>
              <MenuItem
                value="en"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                English
              </MenuItem>
              <MenuItem
                value="es"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Spanish
              </MenuItem>
              <MenuItem
                value="hi"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Hindi
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                  sx: {
                    "& .MuiMenu-list": {
                      padding: "0px",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}>
              <MenuItem
                value=""
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                All
              </MenuItem>
              <MenuItem
                value="marketing"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Marketing
              </MenuItem>
              <MenuItem
                value="transactional"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Transactional
              </MenuItem>
              <MenuItem
                value="utility"
                sx={{
                  p: "2px 8px",
                  minHeight: "32px",
                  "&:hover": { borderRadius: 0 },
                  "&.Mui-selected": { borderRadius: 0 },
                }}>
                Utility
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}>
              <Button
                size="small"
                variant="text"
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges}
                sx={{
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "6px 12px",
                  minWidth: "auto",
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Apply
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "6px 12px",
                  minWidth: "auto",
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Clear
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
              }}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={cursorHistory.length === 0}
                sx={{
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                    cursor: "pointer",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "80px",
                  textAlign: "center",
                  fontSize: "0.85rem",
                }}>
                Page {cursorHistory.length + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={!pagination.hasNext}
                sx={{
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                    cursor: "pointer",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Table */}
      {loading ? (
        <TableLoader message="Loading templates..." />
      ) : templates.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.text.secondary,
          }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(139, 95, 191, 0.1) 0%, rgba(124, 77, 255, 0.1) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}>
            <FiMessageSquare size={40} color={colors.primary.main} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: "#000000",
              mb: 1,
              fontWeight: 600,
              fontFamily: '"Poppins", sans-serif',
            }}>
            No templates found
          </Typography>
          <Typography variant="body1" sx={{ color: "#737B8B" }}>
            {searchTerm || statusFilter || languageFilter || categoryFilter
              ? "Try adjusting your search or filters"
              : "Create your first WhatsApp template to get started"}
          </Typography>
        </Box>
      ) : (
        <Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "4px",
              boxShadow: "none",
              border: `1px solid ${colors.border.main}`,
              overflow: "auto",
            }}>
            <Table>
              <TableHead sx={{ borderBottom: "1px solid #E5E7EB" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "200px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}>
                    Template Name
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "300px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}>
                    Body
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "120px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}>
                    Language
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "120px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "120px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}>
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "140px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}>
                    Last Updated
                  </TableCell>
                  {canWrite && (
                    <TableCell
                      sx={{
                        width: "20px",
                        fontWeight: 600,
                        color: colors.text.primary,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow
                    key={template.id}
                    onClick={() => handleRowClick(template)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: colors.action.row,
                      },
                      "&:not(:last-of-type)": {
                        borderBottom: `1px solid ${colors.border.main}`,
                      },
                    }}>
                    <TableCell sx={{ maxWidth: "200px", py: 1.5 }}>
                      <Tooltip title={template.name} arrow placement="top">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                          }}>
                          {getWhatsAppTemplateTypeIcon(template.type, {
                            fontSize: "small",
                            sx: {
                              mr: 1,
                              flexShrink: 0,
                              color: colors.text.secondary,
                            },
                          })}
                          <Typography
                            variant="body2"
                            sx={{
                              color: colors.text.primary,
                              fontSize: "0.875rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {template.name}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: "300px", py: 1.5 }}>
                      <Tooltip
                        title={template.body || "No content"}
                        arrow
                        placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.text.secondary,
                            fontSize: "0.875rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {truncateText(template.body, 80)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ width: "120px", textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: "0.875rem",
                          textTransform: "uppercase",
                        }}>
                        {template.language || "--"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ width: "120px", textAlign: "center", py: 1.5 }}>
                      <Tooltip
                        title={
                          template.status === "REJECTED" && template.rejected_reason
                            ? template.rejected_reason
                            : ""
                        }
                        arrow
                        placement="top">
                        <Chip
                          label={template?.status || "Unknown"}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(template.status).bg,
                            color: getStatusColor(template.status).color,
                            fontWeight: 501,
                            borderRadius: "4px",
                            fontSize: "12px !important",
                            textTransform: "capitalize",
                            cursor: template?.status === "REJECTED" && template?.rejected_reason ? "help" : "default",
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ width: "120px", textAlign: "center", py: 1.5 }}>
                      <Chip
                        label={template.category || "Uncategorized"}
                        size="small"
                        sx={{
                          backgroundColor: `${colors.primary.main}15`,
                          color: colors.primary.main,
                          fontWeight: 501,
                          borderRadius: "4px",
                          fontSize: "12px !important",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ width: "140px", textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: "0.875rem",
                        }}>
                        {template?.last_updated_time
                          ? formatDateForDisplay(template?.last_updated_time)
                          : "--"}
                      </Typography>
                    </TableCell>
                    {canWrite && (
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ py: 1.5 }}>
                        <Box
                          sx={{ display: "flex", gap: "4px", minWidth: "72px" }}>
                          <IconButton
                            size="small"
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}
                            onClick={() => navigate(`/whatsapp-templates/edit/${template.id}`)}
                            title="Edit">
                            <FiEdit2 size={16} />
                          </IconButton>
                          <Tooltip
                            title={
                              template.can_delete === false
                                ? "Cannot delete - used in active campaigns"
                                : "Delete"
                            }
                            arrow>
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: colors.error.main,
                                  },
                                  "&:disabled": {
                                    opacity: 0.4,
                                    cursor: "not-allowed",
                                  },
                                }}
                                onClick={() => handleDeleteClick(template)}
                                disabled={template.can_delete === false}>
                                <FiTrash2 size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bottom Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 2,
              gap: 1,
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={cursorHistory.length === 0}
                sx={{
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                    cursor: "pointer",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "80px",
                  textAlign: "center",
                  fontSize: "0.85rem",
                }}>
                Page {cursorHistory.length + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={!pagination.hasNext}
                sx={{
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                    cursor: "pointer",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="template"
        deleting={loading}
        maxWidth="520px">
        <WhatsAppTemplateDeleteContent />
      </DeleteConfirmationDialog>
    </Box>
  );
}
